import { flatRatePlanNames, FLAT_RATE_PLANS } from "@common/flatRateBilling";
import {
    currencyFormatterShort,
    currencyFormatterNoFractions,
} from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import {
    getOrganizationAssignablePlans,
    OrganizationAssignablePlan,
} from "@common/constants/organization-billing";
import { ClientPricing } from "@hooks/billing/useClientPricing";
import { ReactNode } from "react";
import { isOrganizationOwnedComplimentaryPlan } from "./organizationOwnedSeatPricing";

type PricingByPlan = Record<FLAT_RATE_PLANS, number>;

interface GetAvailablePlansOptions {
    isOrganizationOwned?: boolean;
    includeRestrictedPlans?: boolean;
}

interface PlanOption {
    value: OrganizationAssignablePlan;
    addon: ReactNode;
    description: ReactNode;
}

interface PlanStepOption {
    label: string;
    addon: ReactNode;
    description: ReactNode;
    value: OrganizationAssignablePlan;
    isComplimentary: boolean;
}

export const getAvailablePlans = (
    allowedPlans?: FLAT_RATE_PLANS[] | null,
    options?: GetAvailablePlansOptions,
): OrganizationAssignablePlan[] =>
    getOrganizationAssignablePlans(allowedPlans, options);

const planDescription = (plan: OrganizationAssignablePlan): ReactNode => {
    switch (plan) {
        case FLAT_RATE_PLANS.FREE:
            return (
                <>
                    Single entity. Auto categorization, profit & loss, balance
                    sheet, receipt matching. Limited to 250 transactions
                    annually.
                </>
            );
        case FLAT_RATE_PLANS.BASIC:
            return (
                <>
                    Single entity. Everything on Free, plus importer, statement
                    sync, reconciliation, and MCP/CLI.
                </>
            );
        case FLAT_RATE_PLANS.PLUS:
            return (
                <>
                    Multi-entity. Everything on Basic, plus classes, accrual
                    ledger, and AR/AP automation. Additional entities with more
                    than 250 transactions annually are $50/m.
                </>
            );
        case FLAT_RATE_PLANS.ADVANCED:
            return (
                <>
                    Multi-entity. Everything on Plus, with multi-entity
                    consolidation and automated intercompany eliminations.
                    Additional entities with more than 250 transactions annually
                    are $50/m.
                </>
            );
        default:
            return null;
    }
};

interface MonthlyPriceDisplayParams {
    amountCents: number;
    originalAmountCents: number;
}

const monthlyPriceDisplay = ({
    amountCents,
    originalAmountCents,
}: MonthlyPriceDisplayParams): ReactNode => (
    <div>
        {originalAmountCents > amountCents && (
            <small>
                <s>
                    {currencyFormatterNoFractions.format(
                        fromIntToMonetary(originalAmountCents),
                    )}
                </s>
            </small>
        )}
        <header>
            {currencyFormatterNoFractions.format(
                fromIntToMonetary(amountCents),
            )}
        </header>
        <small>Billed monthly</small>
    </div>
);

interface PlanPricingDisplayParams {
    plan: OrganizationAssignablePlan;
    pricing: PricingByPlan;
    originalPricing: PricingByPlan;
    isOrgOwned: boolean;
}

const planPricingDisplay = ({
    plan,
    pricing,
    originalPricing,
    isOrgOwned,
}: PlanPricingDisplayParams): ReactNode | null => {
    const { FREE, BASIC, PLUS, ADVANCED } = pricing;

    if (isOrgOwned) {
        switch (plan) {
            case FLAT_RATE_PLANS.FREE:
                return (
                    <div>
                        <header>{currencyFormatterShort.format(FREE)}</header>
                    </div>
                );
            case FLAT_RATE_PLANS.BASIC:
            case FLAT_RATE_PLANS.PLUS:
            case FLAT_RATE_PLANS.ADVANCED:
                return monthlyPriceDisplay({
                    amountCents: pricing[plan],
                    originalAmountCents: originalPricing[plan],
                });
            default:
                return null;
        }
    }

    switch (plan) {
        case FLAT_RATE_PLANS.FREE:
            return (
                <div>
                    <header>{currencyFormatterNoFractions.format(FREE)}</header>
                </div>
            );
        case FLAT_RATE_PLANS.BASIC:
            return (
                <div>
                    <header>
                        {currencyFormatterNoFractions.format(
                            fromIntToMonetary(BASIC),
                        )}
                    </header>
                    <small>Billed annually</small>
                </div>
            );
        case FLAT_RATE_PLANS.PLUS:
            return (
                <div>
                    <header>
                        {currencyFormatterNoFractions.format(
                            fromIntToMonetary(PLUS),
                        )}
                    </header>
                    <small>Billed monthly</small>
                </div>
            );
        case FLAT_RATE_PLANS.ADVANCED:
            return (
                <div>
                    <header>
                        {currencyFormatterNoFractions.format(
                            fromIntToMonetary(ADVANCED),
                        )}
                    </header>
                    <small>Billed monthly</small>
                </div>
            );
        default:
            return null;
    }
};

interface GetPlanOptionsParams {
    pricing: PricingByPlan;
    originalPricing: PricingByPlan;
    isOrgOwned: boolean;
    allowedPlans?: FLAT_RATE_PLANS[] | null;
}

const getPlanOptions = ({
    pricing,
    originalPricing,
    isOrgOwned,
    allowedPlans,
}: GetPlanOptionsParams): PlanOption[] => {
    const availablePlans = getAvailablePlans(allowedPlans, {
        isOrganizationOwned: isOrgOwned,
        includeRestrictedPlans: false,
    });

    const options: PlanOption[] = [];

    for (const plan of availablePlans) {
        const addon = planPricingDisplay({
            plan,
            pricing,
            originalPricing,
            isOrgOwned,
        });
        const description = planDescription(plan);

        if (addon != null && description != null) {
            options.push({
                value: plan,
                addon: addon as NonNullable<ReactNode>,
                description: description as NonNullable<ReactNode>,
            });
        }
    }

    return options;
};

export const buildPlanStepOptions = ({
    clientPricing,
    isOrgOwned,
    allowedPlans,
}: {
    clientPricing: ClientPricing;
    isOrgOwned: boolean;
    allowedPlans?: FLAT_RATE_PLANS[] | null;
}): PlanStepOption[] => {
    const pricing = isOrgOwned
        ? clientPricing.organizationOwnedNextSeatPricing
        : clientPricing.clientOwnedBillingPricing;
    const originalPricing = isOrgOwned
        ? clientPricing.organizationOwnedNextSeatPricingBeforeDiscount
        : pricing;

    return getPlanOptions({
        pricing,
        originalPricing,
        isOrgOwned,
        allowedPlans,
    }).map((option) => ({
        label: flatRatePlanNames[option.value],
        addon: option.addon,
        description: option.description,
        value: option.value,
        isComplimentary:
            isOrgOwned &&
            isOrganizationOwnedComplimentaryPlan({
                clientPricing,
                plan: option.value,
            }),
    }));
};

export const isOrganizationOwnedPricingBlocked = ({
    clientPricing,
    isOrgOwned,
}: {
    clientPricing: ClientPricing;
    isOrgOwned: boolean;
}): boolean => isOrgOwned && !clientPricing.isOrganizationOwnedPricingReady;

export const getAutoSelectedPlan = (
    newIsOrgOwned: boolean,
    currentPlan: FLAT_RATE_PLANS,
    allowedPlans?: FLAT_RATE_PLANS[] | null,
): OrganizationAssignablePlan | null => {
    const newAvailablePlans = getAvailablePlans(allowedPlans, {
        isOrganizationOwned: newIsOrgOwned,
        includeRestrictedPlans: false,
    });

    if (!newAvailablePlans.some((plan) => plan === currentPlan)) {
        if (
            newIsOrgOwned &&
            newAvailablePlans.some((plan) => plan === FLAT_RATE_PLANS.PLUS)
        ) {
            return FLAT_RATE_PLANS.PLUS;
        }
        return newAvailablePlans[0] ?? null;
    }

    return null;
};
